import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useAuth0 } from '@auth0/auth0-react';
import { isTeamMemberAuthenticated, getTeamUser } from '../../services/teamAuthService';
import { BsBug, BsLightbulb, BsArrowUp, BsQuestionCircle, BsThreeDots, BsSend, BsClock, BsPersonCircle, BsShieldCheck } from 'react-icons/bs';

const PageContainer = styled.div`
  min-height: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  padding: 32px 24px 24px;
  color: #e2e8f0;
  overflow-y: auto;
`;

const ContentCard = styled.div`
  background: rgba(30, 35, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  max-width: 800px;
  margin: 0 auto;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #f1f5f9;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$bg || 'rgba(99, 102, 241, 0.15)'};
  color: ${props => props.$color || '#a5b4fc'};
  border: 1px solid ${props => props.$border || 'rgba(99, 102, 241, 0.3)'};
`;

const Section = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const SectionLabel = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0 0 6px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Description = styled.p`
  background: rgba(15, 20, 30, 0.5);
  padding: 14px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  color: #cbd5e1;
  margin: 0;
`;

const CommentsSection = styled.div`
  padding: 20px 24px;
`;

const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #94a3b8;
`;

const MessagesContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 16px;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  align-self: ${props => props.$isAdmin ? 'flex-start' : 'flex-end'};
  background: ${props => props.$isAdmin ? 'rgba(139, 92, 246, 0.12)' : 'rgba(59, 130, 246, 0.12)'};
  border: 1px solid ${props => props.$isAdmin ? 'rgba(139, 92, 246, 0.25)' : 'rgba(59, 130, 246, 0.25)'};
`;

const MessageAuthor = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$isAdmin ? '#a78bfa' : '#60a5fa'};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MessageTime = styled.div`
  font-size: 10px;
  color: #475569;
  margin-top: 4px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const MessageInput = styled.textarea`
  flex: 1;
  background: rgba(15, 20, 30, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 8px;
  padding: 10px 14px;
  color: #e2e8f0;
  font-size: 14px;
  resize: none;
  outline: none;
  min-height: 44px;
  max-height: 120px;
  font-family: inherit;

  &:focus {
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: #475569;
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const EmptyState = styled.p`
  text-align: center;
  color: #475569;
  font-size: 13px;
  padding: 24px 0;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #64748b;
  font-size: 14px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #f87171;
  font-size: 14px;
  gap: 8px;
`;

const ScreenshotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 8px;
`;

const ScreenshotImg = styled.a`
  display: block;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
  }

  img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    display: block;
  }
`;

const typeConfig = {
  bug: { label: 'Bug', icon: <BsBug />, color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)', border: 'rgba(248, 113, 113, 0.3)' },
  suggestion: { label: 'Suggestion', icon: <BsLightbulb />, color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.3)' },
  improvement: { label: 'Improvement', icon: <BsArrowUp />, color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)', border: 'rgba(52, 211, 153, 0.3)' },
  question: { label: 'Question', icon: <BsQuestionCircle />, color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)', border: 'rgba(96, 165, 250, 0.3)' },
  other: { label: 'Other', icon: <BsThreeDots />, color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)', border: 'rgba(167, 139, 250, 0.3)' }
};

const statusConfig = {
  new: { label: 'New', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)', border: 'rgba(96, 165, 250, 0.3)' },
  in_review: { label: 'In Review', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.3)' },
  in_progress: { label: 'In Progress', color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)', border: 'rgba(167, 139, 250, 0.3)' },
  resolved: { label: 'Resolved', color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)', border: 'rgba(52, 211, 153, 0.3)' },
  closed: { label: 'Closed', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)', border: 'rgba(148, 163, 184, 0.3)' },
  wont_fix: { label: "Won't Fix", color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)', border: 'rgba(248, 113, 113, 0.3)' }
};

const priorityConfig = {
  low: { label: 'Low', color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)', border: 'rgba(52, 211, 153, 0.3)' },
  medium: { label: 'Medium', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.3)' },
  high: { label: 'High', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.12)', border: 'rgba(251, 146, 60, 0.3)' },
  critical: { label: 'Critical', color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)', border: 'rgba(248, 113, 113, 0.3)' }
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString();
};

export default function FeedbackDetailPage() {
  const { id } = useParams();
  const apiClient = useAuthenticatedAxios();
  const { user } = useAuth0();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const currentUserName = (() => {
    if (isTeamMemberAuthenticated()) {
      const teamUser = getTeamUser();
      return teamUser?.name || teamUser?.email || 'Team Member';
    }
    return user?.name || user?.email || 'User';
  })();

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/bug-reports/${id}`);
      setReport(res.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
      setError('Failed to load feedback. It may have been deleted or you may not have access.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchReport();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [report?.comments]);

  const handleSendComment = async () => {
    if (!commentText.trim() || sending) return;

    setSending(true);
    try {
      await apiClient.post(`/bug-reports/${id}/comments`, {
        message: commentText.trim(),
        author: currentUserName,
        authorRole: 'user',
        sendNotification: true
      });
      setCommentText('');
      await fetchReport();
    } catch (err) {
      console.error('Failed to send comment:', err);
    }
    setSending(false);
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentCard>
          <LoadingState>Loading feedback...</LoadingState>
        </ContentCard>
      </PageContainer>
    );
  }

  if (error || !report) {
    return (
      <PageContainer>
        <ContentCard>
          <ErrorState>
            <span>{error || 'Feedback not found'}</span>
          </ErrorState>
        </ContentCard>
      </PageContainer>
    );
  }

  const type = typeConfig[report.type] || typeConfig.other;
  const status = statusConfig[report.status] || statusConfig.new;
  const priority = priorityConfig[report.priority] || priorityConfig.medium;

  return (
    <PageContainer>
      <ContentCard>
        <Header>
          <Title>{type.icon} {report.title}</Title>
          <BadgeRow>
            <Badge $bg={type.bg} $color={type.color} $border={type.border}>
              {type.label}
            </Badge>
            <Badge $bg={priority.bg} $color={priority.color} $border={priority.border}>
              {priority.label}
            </Badge>
            <Badge $bg={status.bg} $color={status.color} $border={status.border}>
              {status.label}
            </Badge>
          </BadgeRow>
        </Header>

        <Section>
          <SectionLabel>Description</SectionLabel>
          <Description>{report.description}</Description>
        </Section>

        <Section>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <SectionLabel>Submitted By</SectionLabel>
              <div style={{ fontSize: '14px' }}>{report.userName}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{report.userEmail}</div>
            </div>
            <div>
              <SectionLabel>Submitted</SectionLabel>
              <div style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <BsClock style={{ fontSize: '12px', color: '#64748b' }} />
                {formatDate(report.createdAt)}
              </div>
            </div>
          </div>
        </Section>

        {(report.screenshots?.length > 0) && (
          <Section>
            <SectionLabel>Screenshots ({report.screenshots.length})</SectionLabel>
            <ScreenshotsGrid>
              {report.screenshots.map((url, i) => (
                <ScreenshotImg key={i} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Screenshot ${i + 1}`} />
                </ScreenshotImg>
              ))}
            </ScreenshotsGrid>
          </Section>
        )}

        <CommentsSection>
          <CommentsHeader>
            <BsPersonCircle />
            Messages ({report.comments?.length || 0})
          </CommentsHeader>

          {(report.comments?.length > 0) ? (
            <MessagesContainer>
              {report.comments.map((comment, index) => (
                <MessageBubble key={comment._id || index} $isAdmin={comment.authorRole === 'admin'}>
                  <MessageAuthor $isAdmin={comment.authorRole === 'admin'}>
                    {comment.author}
                    {comment.authorRole === 'admin' && <BsShieldCheck style={{ fontSize: '11px' }} />}
                  </MessageAuthor>
                  <div style={{ whiteSpace: 'pre-wrap', color: '#cbd5e1' }}>{comment.message}</div>
                  <MessageTime>{formatDate(comment.createdAt)}</MessageTime>
                </MessageBubble>
              ))}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          ) : (
            <EmptyState>No messages yet. Start the conversation below.</EmptyState>
          )}

          <InputRow>
            <MessageInput
              placeholder="Type a message..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendComment();
                }
              }}
              rows={1}
            />
            <SendButton
              onClick={handleSendComment}
              disabled={!commentText.trim() || sending}
            >
              <BsSend />
            </SendButton>
          </InputRow>
        </CommentsSection>
      </ContentCard>
    </PageContainer>
  );
}
